import "./quick_apply_flash.scss";
import { Body, Link } from "../components/typography";
import CheckIcon from "../components/icons/check";
import ErrorIcon from "../components/icons/error";
import { useTranslation } from "react-i18next";
import { Flash } from "~/components/flash";
import { type CompletionStatus } from "./types";

export type AutofillStatus = "success" | "error";

type QuickApplyFlashProps = {
  autofillStatus: AutofillStatus;
  completionStatus: CompletionStatus | null;
  closeFlash: () => void;
  embedded?: boolean;
};

interface MessageProps {
  message: string;
  url: string;
  linkText: string | null;
}

interface StatusProps {
  autofillStatus: AutofillStatus;
  completionStatus: CompletionStatus | null;
}

function isConsideredComplete(completionStatus: CompletionStatus | null): boolean {
  if (!completionStatus) {
    return false;
  }

  return ["almost_finished", "complete"].includes(completionStatus);
}

const jobSeekersUrl = (path: string) => `${window.ENV.JOB_SEEKERS_URL}${path}`;
const jobSeekersProfileUrl = () => jobSeekersUrl("/profile");
const jobSeekersQuickApplyUrl = () => jobSeekersUrl("/quick_apply");

const jobSeekersUrlFor = (
  autofillStatus: AutofillStatus,
  completionStatus: CompletionStatus | null
): string => {
  if (autofillStatus === "error" || isConsideredComplete(completionStatus)) {
    return jobSeekersProfileUrl();
  }

  return jobSeekersQuickApplyUrl();
};

const Icon = ({ autofillStatus, completionStatus }: StatusProps) => {
  if (autofillStatus === "error" || completionStatus === "not_started") {
    return <ErrorIcon color="red" />;
  }

  return <CheckIcon color="green" />;
};

function useAutofillMessage(
  autofillStatus: AutofillStatus,
  completionStatus: CompletionStatus | null
): string {
  const { t } = useTranslation("job_post");

  if (autofillStatus === "error") {
    return t("application.quick_apply.error");
  }

  switch (completionStatus) {
    case "not_started":
      return t("application.quick_apply.not_started");
    case "in_progress":
      return t("application.quick_apply.in_progress");
    default:
      return t("application.quick_apply.success");
  }
}

function useAutofillLinkText(
  autofillStatus: AutofillStatus,
  completionStatus: CompletionStatus | null
): string | null {
  const { t } = useTranslation("job_post");

  if (autofillStatus === "error") {
    return null;
  }

  switch (completionStatus) {
    case "not_started":
      return t("application.quick_apply.my_greenhouse_profile");
    case "in_progress":
      return t("application.quick_apply.quick_apply_profile");
    default:
      return "MyGreenhouse";
  }
}

const Message = ({ message, linkText, url }: MessageProps) => {
  if (!linkText) {
    return <Body>{message}</Body>;
  }

  return (
    <Body>
      {message}{" "}
      <Link href={url} target="_blank">
        {linkText}
      </Link>
    </Body>
  );
};

export const QuickApplyFlash = ({
  autofillStatus,
  completionStatus,
  closeFlash,
  embedded,
}: QuickApplyFlashProps) => {
  const url = jobSeekersUrlFor(autofillStatus, completionStatus);
  const messageText = useAutofillMessage(autofillStatus, completionStatus);
  const linkText = useAutofillLinkText(autofillStatus, completionStatus);

  if (embedded) {
    window.parent?.postMessage(
      {
        type: "autofill",
        autofillStatus,
        completionStatus,
        messageText,
        linkText,
        editProfileLink: url,
      },
      "*"
    );

    return null;
  }

  const backgroundColor = autofillStatus === "error" ? "extra-light-red" : "paler-gray";
  const showCloseIcon = autofillStatus !== "error";

  return (
    <Flash
      icon={<Icon autofillStatus={autofillStatus} completionStatus={completionStatus} />}
      backgroundColor={backgroundColor}
      message={<Message message={messageText} linkText={linkText} url={url} />}
      showCloseIcon={showCloseIcon}
      closeFlash={closeFlash}
    />
  );
};
